import React, { createContext, useContext, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export const MAX_ID_KEY = 'max_id';
export const TG_ID_KEY = 'tg_id';

/**
 * Проверяет, является ли устройство iOS
 */
export function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Получить значение cookie по имени
 */
export function getCookieValue(name) {
    const matches = document.cookie.match(new RegExp(
        '(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'
    ));
    return matches ? decodeURIComponent(matches[1]) : null;
}

/**
 * Установить cookie с правильными флагами для iOS PWA
 * Согласно статье на Habr, cookies копируются при установке PWA на iOS,
 * в отличие от localStorage который НЕ копируется
 */
export function setCookie(name, value) {
    // Устанавливаем cookie на год с правильными флагами
    // SameSite=Lax - обязательно для работы с iOS PWA
    // path=/ - чтобы cookie была доступна на всех страницах
    document.cookie = `${name}=${value}; path=/; max-age=31536000; SameSite=Lax`;
    console.log(`🍪 Cookie set: ${name}=${value}`);
}

export function detectPlatform() {
    // return 'max';
    // return 'max';
    // return 'web';
    // TelegramWebviewProxy — нативный объект, который инжектит Telegram-клиент.
    // initData может быть пустой строкой при запуске с домашнего экрана,
    // поэтому проверяем наличие нативного прокси как надёжный признак Telegram.
    if (window.Telegram?.WebApp?.initData || window.TelegramWebviewProxy) return 'telegram';
    // В MAX initData тоже может быть пустым, поэтому опираемся на сам объект WebApp
    if (window.WebApp?.initData) return 'max';
    // MAX-пользователь, открывший PWA из браузера — SDK нет
    if(getMaxIdFromUrl() || getMaxIdFromStorage()) return 'max';
    // Telegram-пользователь, открывший PWA из браузера — SDK нет
    if(getTgIdFromUrl() || getTgIdFromStorage()) return 'telegram';
    return 'web';
}

export function getMaxIdFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(MAX_ID_KEY);
}

export function saveMaxIdToStorage(maxId) {
    if (maxId) {
        const ios = isIOS();
        
        // На iOS ВСЕГДА сохраняем в cookies (они копируются при установке PWA)
        // На других платформах сохраняем в localStorage
        if (ios) {
            setCookie(MAX_ID_KEY, String(maxId));
            console.log(`📱 iOS detected: saved max_id to cookie (will be copied on PWA install)`);
        } else {
            localStorage.setItem(MAX_ID_KEY, String(maxId));
            console.log(`💾 Non-iOS: saved max_id to localStorage`);
        }
    }
}

export function getMaxIdFromStorage() {
    const ios = isIOS();
    
    // На iOS читаем из cookies (localStorage не копируется при установке PWA)
    if (ios) {
        const fromCookie = getCookieValue(MAX_ID_KEY);
        if (fromCookie) {
            console.log(`🍪 iOS: Retrieved max_id from cookie: ${fromCookie}`);
            return fromCookie;
        }
    }
    
    // Fallback на localStorage для других платформ или если cookie пустая
    const fromStorage = localStorage.getItem(MAX_ID_KEY);
    if (fromStorage) {
        console.log(`💾 Retrieved max_id from localStorage: ${fromStorage}`);
    }
    return fromStorage;
}

export function getTgIdFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(TG_ID_KEY);
}

export function saveTgIdToStorage(tgId) {
    if (tgId) {
        const ios = isIOS();
        
        // На iOS ВСЕГДА сохраняем в cookies (они копируются при установке PWA)
        // На других платформах сохраняем в localStorage
        if (ios) {
            setCookie(TG_ID_KEY, String(tgId));
            console.log(`📱 iOS detected: saved tg_id to cookie (will be copied on PWA install)`);
        } else {
            localStorage.setItem(TG_ID_KEY, String(tgId));
            console.log(`💾 Non-iOS: saved tg_id to localStorage`);
        }
    }
}

export function getTgIdFromStorage() {
    const ios = isIOS();
    
    // На iOS читаем из cookies (localStorage не копируется при установке PWA)
    if (ios) {
        const fromCookie = getCookieValue(TG_ID_KEY);
        if (fromCookie) {
            console.log(`🍪 iOS: Retrieved tg_id from cookie: ${fromCookie}`);
            return fromCookie;
        }
    }
    
    // Fallback на localStorage для других платформ или если cookie пустая
    const fromStorage = localStorage.getItem(TG_ID_KEY);
    if (fromStorage) {
        console.log(`💾 Retrieved tg_id from localStorage: ${fromStorage}`);
    }
    return fromStorage;
}

function mapUser(raw) {
    if (!raw) return null;
    return {
        id: raw.id,
        firstName: raw.first_name,
        lastName: raw.last_name,
        username: raw.username,
        languageCode: raw.language_code,
        photoUrl: raw.photo_url ?? null,
    };
}

const MessengerContext = createContext(null);

export const useMessenger = () => {
    const ctx = useContext(MessengerContext);
    if (!ctx) {
        throw new Error('useMessenger must be used within MessengerProvider');
    }
    return ctx;
};

export const MessengerProvider = ({ children }) => {
    const backButtonHandlerRef = useRef(null);
    const navigate = useNavigate();

    // При монтировании провайдера проверяем URL на наличие max_id / tg_id и сохраняем
    useEffect(() => {
        const maxIdFromUrl = getMaxIdFromUrl();
        if (maxIdFromUrl) {
            saveMaxIdToStorage(maxIdFromUrl);
        }
        const tgIdFromUrl = getTgIdFromUrl();
        if (tgIdFromUrl) {
            saveTgIdToStorage(tgIdFromUrl);
        }
    }, []);

    const value = useMemo(() => {
        const platform = detectPlatform();

        let webapp = null;
        if (platform === 'telegram') {
            webapp = window.Telegram?.WebApp ?? null;
        } else if (platform === 'max') {
            webapp = window.WebApp ?? null;
        }

        const initDataUnsafe = webapp?.initDataUnsafe ?? {};
        const initData = webapp?.initData ?? '';
        const nativePlatform = webapp?.platform ?? 'web';

        // Для MAX сначала пробуем взять из initDataUnsafe, затем из URL, затем из хранилища
        let userId = initDataUnsafe?.user?.id;
        if (platform === 'max' && !userId) {
            userId = getMaxIdFromUrl() || getMaxIdFromStorage();
        }
        // Проверяем tg_id из URL/хранилища (пользователь пришёл из Telegram через внешний браузер)
        if ((platform === 'web' || platform === 'telegram') && !userId) {
            userId = getTgIdFromUrl() || getTgIdFromStorage();
        }
        // Fallback для разработки
        // if (!userId) {
        //     userId = 321611257;
        // }
        
        const user = mapUser(initDataUnsafe?.user);

        const close = () => {
            if (platform === 'telegram' && webapp) {
                navigate('/menu');

            } else if (platform === 'max' && webapp) {
                navigate('/menu');
            } else {
                navigate('/menu');
            }
        };

        const goToMenu = () => {
            navigate('/menu');
        };

        const expand = () => {
            if (platform === 'telegram' && webapp) {
                webapp.expand();
            }
        };

        const openLink = (url) => {
            let finalUrl = url;

            // Для MAX добавляем max_id, для Telegram — tg_id в query параметры
            if ((platform === 'max' || platform === 'telegram') && userId) {
                try {
                    const urlObj = new URL(url, window.location.origin);
                    const key = platform === 'max' ? MAX_ID_KEY : TG_ID_KEY;
                    urlObj.searchParams.set(key, String(userId));
                    finalUrl = urlObj.toString();
                } catch (e) {
                    console.error('Error adding user ID to URL:', e);
                }
            }

            if (webapp?.openLink) {
                webapp.openLink(finalUrl);
            } else {
                window.open(finalUrl, '_blank');
            }
        };

        const showBackButton = (onClick) => {
            if (!webapp?.BackButton) return;
            webapp.BackButton.show();
            if (platform === 'telegram' || platform === 'max') {
                webapp.BackButton.onClick(onClick);
            }
            backButtonHandlerRef.current = onClick;
        };

        const hideBackButton = () => {
            if (!webapp?.BackButton) return;
            webapp.BackButton.hide();
            const handler = backButtonHandlerRef.current;
            if (handler) {
                if (platform === 'telegram') {
                    webapp.BackButton.offClick(handler);
                } else if (platform === 'max') {
                    webapp.offEvent('back_button_pressed', handler);
                }
                backButtonHandlerRef.current = null;
            }
        };

        return {
            platform,
            nativePlatform,
            userId,
            user,
            initData,
            initDataUnsafe,
            navigate,
            close,
            expand,
            goToMenu,
            openLink,
            showBackButton,
            hideBackButton,
        };
    }, [navigate]);

    useEffect(() => {
        const { platform } = value;
        if (platform === 'max' && window.WebApp) {
            window.WebApp.ready();
        }
        if (platform === 'telegram') {
            // ready() нужно вызывать как можно раньше — без этого
            // Telegram-клиент может закрыть мини-приложение по таймауту
            // (особенно актуально при запуске с иконки на рабочем столе)
            window.Telegram?.WebApp?.ready?.();
            value.expand();
        }
    }, [value]);

    return (
        <MessengerContext.Provider value={value}>
            {children}
        </MessengerContext.Provider>
    );
};
